import type { Credential, Project, Role, SkillBar, SkillGroup } from './types';

export const site = {
  url: 'https://palbirmin.github.io/',
  title: 'Palbir Minhas — Software Engineer',
  description:
    'Software engineer in Seattle working on backend systems, distributed systems, and cloud infrastructure. UW Computer Science 2024, Georgia Tech MS in progress.',
} as const;

export const profile = {
  name: 'Palbir Minhas',
  role: 'Software Engineer',
  location: 'Seattle, WA',
  email: 'palbirminhas@gmail.com',
  github: 'https://github.com/palbirmin',
  linkedin: 'https://www.linkedin.com/in/palbirmin/',
  resume: 'Palbir_Minhas_Resume.pdf',
  availability: 'Open to new-grad and entry-level software engineering roles',
  /** Sits under the wordmark in the hero, where the old site said "Portfolio". */
  label: 'Portfolio',
  intro:
    'Software engineer in Seattle building backend systems and the infrastructure underneath them — consensus protocols, concurrent servers, and the test suites that keep them honest.',
} as const;

export const socials = [
  { id: 'github', label: 'GitHub', href: 'https://github.com/palbirmin' },
  { id: 'linkedin', label: 'LinkedIn', href: 'https://www.linkedin.com/in/palbirmin/' },
  { id: 'mail', label: 'Email', href: 'mailto:palbirminhas@gmail.com' },
] as const;

export const about = [
  'I earned a Bachelor of Science in Computer Science from the University of Washington in 2024, and I am partway through a Master of Science in Computer Science at Georgia Tech. Most of what holds my attention sits close to the machine: correctness under concurrency, how a distributed system behaves once nodes stop answering, and building verification into a design instead of bolting it on afterwards.',
  'That bias shows up in the work below. A key-value store is only interesting if it stays linearizable while nodes crash. A backtesting engine is only worth running if its tests prove it cannot see the future. Most of these projects are here because of an invariant I had to defend, not because of a framework I wanted to try.',
];

export const credentials: Credential[] = [
  {
    id: 'gatech',
    school: 'Georgia Institute of Technology',
    award: 'Master of Science, Computer Science',
    detail: '4.0 program GPA · Knowledge-Based AI, Machine Learning for Trading',
    period: '2026 — Present',
  },
  {
    id: 'uw',
    school: 'University of Washington',
    award: 'Bachelor of Science, Computer Science',
    detail: "3.65 GPA · Dean's List, six quarters · Paul G. Allen School",
    period: '2021 — 2024',
  },
  {
    id: 'aws',
    school: 'Amazon Web Services',
    award: 'AWS Certified Cloud Practitioner',
    detail: 'Valid through October 2027',
    period: '2024',
  },
];

export const projects: Project[] = [
  {
    id: 'neza-bazi',
    title: 'Neza Bazi Punjab Championship',
    period: '2026 — Present',
    context: 'Lead developer, two-engineer team',
    summary:
      'The official registration and event platform for an international tent-pegging championship in Amritsar, Punjab, expecting 150+ riders from 6 countries.',
    detail:
      'Solo and four-rider registration run through a multi-step wizard with database-backed draft autosave that survives a logout and a change of device. Authentication covers email/password and Google OAuth with email confirmation, self-service reset, and account-collision handling. Administrators hold no direct write access to any table — every privileged mutation goes through a server-only route into a transactional Postgres function that commits the data change and its audit record together, with the actor read from the verified server session rather than from the client.',
    metrics: [
      '34 routes',
      '50 automated tests',
      '5 tables · 7 functions · 11 migrations',
      'RLS on every table',
    ],
    stack: ['Next.js 16', 'React 19', 'TypeScript', 'Supabase', 'PostgreSQL', 'Tailwind CSS', 'Vercel'],
    links: [
      { kind: 'live', label: 'nezabazipunjab.com', href: 'https://nezabazipunjab.com' },
      { kind: 'code', label: 'Source', href: 'https://github.com/palbirmin/NezaBazi' },
    ],
  },
  {
    id: 'mnq',
    title: 'MNQ Futures Backtesting & Research Framework',
    period: '2025 — Present',
    context: 'Independent research since 2025, implementation since 2026',
    summary:
      'An event-driven futures backtesting and quantitative research framework built from scratch, with realism rather than flattering backtest numbers as the design goal.',
    detail:
      'The simulation engine owns order fills, position state, stops and targets, risk limits, commissions, and PnL accounting. Its correctness invariants are enforced by tests rather than by convention: an order placed on bar N executes on bar N+1, nothing anywhere reads ahead of the current bar, and a stop wins a same-bar tie against its target. Above that sit walk-forward grid-search optimization and Monte Carlo trade resampling across up to 50,000 paths. The research interface moved off Streamlit onto a FastAPI and React service with persisted run state and caching keyed on parameters, data-file mtime, and schema version.',
    metrics: ['439 automated tests', '50,000 Monte Carlo paths', 'static architecture guard tests'],
    stack: ['Python', 'FastAPI', 'React', 'TypeScript', 'NumPy', 'pandas', 'Parquet'],
    links: [],
    note: 'Private repository. Research and engineering only — not traded live, and no performance claims attached.',
  },
  {
    id: 'arc-agi',
    title: 'ARC-AGI Symbolic Solver Agent',
    period: '2026',
    context: 'Georgia Tech CS7637, Knowledge-Based AI',
    summary:
      'A generate-and-test agent that induces a grid-transformation rule from two to four example pairs and applies it to unseen inputs, using no machine learning at all.',
    detail:
      'The Abstraction and Reasoning Corpus deliberately supplies far too few examples to learn from statistically, so the agent reasons structurally instead. Twenty-five solver methods sit on an object-detection layer built over SciPy ndimage — connected-component labeling, flood fill, morphological dilation — which powers nine of them. Every rule parameter is derived from the input rather than hard-coded, and a candidate rule is only applied once it verifies by exact match against all of the examples. Every failure is an abstention: the agent never returned a wrong grid.',
    metrics: ['80/96 on the course subset', '40/48 public · 40/48 held out', '0.668s full evaluation'],
    stack: ['Python', 'NumPy', 'SciPy'],
    links: [],
    note: 'Graduate coursework — source kept private under course academic-integrity policy.',
  },
  {
    id: 'paxos',
    title: 'Distributed Key-Value Store on Multi-Paxos',
    period: '2023',
    context: 'University of Washington, CSE 452 Distributed Systems',
    summary:
      'A replicated key-value store implementing Multi-Paxos, guaranteeing linearizable execution across a cluster.',
    detail:
      'A replicated state machine with leader election and automatic failover built out of ballots, heartbeats, and majority quorum. It holds its correctness guarantee while nodes crash and while messages are arbitrarily delayed, reordered, or dropped.',
    metrics: ['linearizable under crash and message loss'],
    stack: ['Java', 'Consensus', 'Replicated state machines'],
    links: [],
    note: 'Coursework — source kept private under course academic-integrity policy.',
  },
  {
    id: 'http-server',
    title: 'Multithreaded HTTP Server',
    period: '2022',
    context: 'University of Washington, CSE 333 Systems Programming',
    summary: 'A concurrent HTTP server written in C++ with optimized request processing.',
    detail:
      'Hardened against cross-site scripting and directory-traversal attacks, and validated for correctness and memory safety under valgrind.',
    metrics: ['clean under valgrind'],
    stack: ['C++', 'POSIX threads', 'valgrind'],
    links: [],
    note: 'Coursework — source kept private under course academic-integrity policy.',
  },
  {
    id: 'microservices',
    title: 'Cloud-Deployed Microservices',
    period: '2023',
    context: 'University of Washington, CSE 453 Datacenter Systems',
    summary:
      'Microservices designed and deployed on Kubernetes to stand in for a production distributed system.',
    detail:
      'Load tested with wrk2 to characterize latency, throughput, and where the system bottlenecks under sustained high traffic.',
    metrics: ['load tested with wrk2'],
    stack: ['Kubernetes', 'Docker', 'wrk2'],
    links: [],
    note: 'Coursework — source kept private under course academic-integrity policy.',
  },
  {
    id: 'wedding',
    title: 'Two-Sided Wedding Site',
    period: '2026',
    context: 'Freelance, live event',
    summary:
      "Guests choose the bride's or groom's side at a gateway page and get that side's schedule, travel details, and RSVP.",
    detail:
      'Nine pages share a single stylesheet built on CSS custom properties, clamp() fluid type, and six breakpoints. Scroll reveal runs on IntersectionObserver with a prefers-reduced-motion override on every animation. Per-page SEO and Open Graph metadata, a custom 404, and RSVP through an embedded form. No build step and no dependencies.',
    metrics: ['9 pages', '6 breakpoints', 'zero dependencies'],
    stack: ['HTML', 'CSS', 'JavaScript', 'GitHub Pages'],
    links: [{ kind: 'live', label: 'darshandroop.com', href: 'https://www.darshandroop.com/' }],
  },
];

export const roles: Role[] = [
  {
    id: 'merrill',
    title: 'Software Systems Engineer',
    org: 'Merrill Fields Inn',
    period: '2024',
    location: 'Remote',
    bullets: [
      'Engineered a PostgreSQL-backed management system with server-side application logic and a React frontend, unifying search across reservations, guests, rooms, and transactions — improving data accuracy roughly 40% and reducing booking conflicts roughly 60%.',
      'Automated backend data pipelines and financial reconciliation in Python on scheduled jobs, cutting manual reconciliation time roughly 70%.',
    ],
  },
  {
    id: 'allen-school',
    title: 'Software Engineer Intern',
    org: 'Paul G. Allen School, University of Washington',
    period: '2022 — 2023',
    location: 'Seattle, WA',
    bullets: [
      'Optimized backend services integrating the Google Maps and Street View APIs in Scala, improving system performance by 212.8% through asynchronous processing and request optimization.',
      'Improved ML pipeline reliability and data validation across 10,000+ sidewalk images for Project Sidewalk, a crowdsourced accessibility platform running in five international cities, on Scala, PostgreSQL, and Docker.',
    ],
  },
];

/**
 * The original site's animated bars, kept — but each one says what it is
 * claiming instead of showing a percentage that nobody can check. Levels track
 * the depth calibration in PROFILE.md, not wishful thinking.
 */
export const skillBars: SkillBar[] = [
  { name: 'Python', level: 92, note: 'Daily driver' },
  { name: 'TypeScript & React', level: 88, note: 'Shipped to production' },
  { name: 'PostgreSQL & SQL', level: 85, note: 'Schema, functions, RLS' },
  { name: 'Java', level: 76, note: 'Distributed systems work' },
  { name: 'C & C++', level: 70, note: 'Systems programming' },
  { name: 'Docker & Kubernetes', level: 55, note: 'One deployed class project' },
  { name: 'AWS', level: 50, note: 'Cloud Practitioner certified' },
];

export const skills: SkillGroup[] = [
  {
    label: 'Languages',
    items: ['Java', 'Python', 'TypeScript', 'JavaScript', 'C++', 'C', 'SQL', 'Scala', 'Bash'],
  },
  {
    label: 'Backend',
    items: [
      'PostgreSQL',
      'MySQL',
      'Node.js',
      'FastAPI',
      'REST APIs',
      'Supabase',
      'Row-Level Security',
      'OAuth',
      'Migrations',
    ],
  },
  {
    label: 'Frontend',
    items: ['React 19', 'Next.js', 'Tailwind CSS', 'TanStack Query', 'Vite', 'ECharts'],
  },
  {
    label: 'Infrastructure',
    items: ['Docker', 'Kubernetes', 'Linux', 'AWS', 'Vercel', 'GitHub Actions', 'Git', 'Parquet'],
  },
  {
    label: 'Testing',
    items: ['pytest', 'Vitest', 'Node test runner', 'Integration suites', 'Architecture guard tests'],
  },
  {
    label: 'Concepts',
    items: [
      'Distributed systems',
      'Consensus',
      'Concurrency',
      'Microservices',
      'Systems programming',
      'Algorithms',
    ],
  },
];

export const sections = [
  { id: 'about', index: '01', label: 'About' },
  { id: 'projects', index: '02', label: 'Projects' },
  { id: 'experience', index: '03', label: 'Experience' },
  { id: 'skills', index: '04', label: 'Skills' },
  { id: 'resume', index: '05', label: 'Résumé' },
  { id: 'contact', index: '06', label: 'Contact' },
] as const;
